/**
 * Hook for managing projects data and operations.
 */

import { useState, useEffect, useCallback } from 'react';
import projectService from '../services/projectService';
import {
  Project,
  ProjectSummary,
  UserProjectSummary,
  CreateProjectRequest,
  UpdateProjectRequest,
  TeamMember,
} from '../types';

export function useProjects() {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await projectService.listProjects();
      setProjects(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load projects');
      console.error('Error loading projects:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createProject = useCallback(async (request: CreateProjectRequest) => {
    setError(null);
    try {
      const result = await projectService.createProject(request);
      await loadProjects(); // Refresh list
      return result;
    } catch (err: any) {
      setError(err.message || 'Failed to create project');
      throw err;
    }
  }, [loadProjects]);

  const deleteProject = useCallback(async (projectId: string) => {
    setError(null);
    try {
      await projectService.deleteProject(projectId);
      await loadProjects(); // Refresh list
    } catch (err: any) {
      setError(err.message || 'Failed to delete project');
      throw err;
    }
  }, [loadProjects]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  return {
    projects,
    loading,
    error,
    loadProjects,
    createProject,
    deleteProject,
  };
}

export function useMyProjects() {
  const [projects, setProjects] = useState<UserProjectSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await projectService.listMyProjects();
      setProjects(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load projects');
      console.error('Error loading my projects:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  return {
    projects,
    loading,
    error,
    loadProjects,
  };
}

export function useProject(projectId: string | null) {
  const [project, setProject] = useState<Project | null>(null);
  const [etag, setEtag] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProject = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await projectService.getProjectWithETag(projectId);
      setProject(data.project);
      setEtag(data.etag);
    } catch (err: any) {
      setError(err.message || 'Failed to load project');
      console.error('Error loading project:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const updateProject = useCallback(async (request: UpdateProjectRequest) => {
    if (!projectId) return;

    setError(null);
    try {
      await projectService.updateProject(projectId, request, etag);
      await loadProject(); // Refresh project data
    } catch (err: any) {
      if (projectService.isConcurrencyError(err)) {
        setError('This project was modified by another user. Please refresh and try again.');
        await loadProject(); // Auto-refresh on concurrency error
      } else {
        setError(err.message || 'Failed to update project');
      }
      throw err;
    }
  }, [projectId, etag, loadProject]);

  const addTeamMember = useCallback(async (member: TeamMember) => {
    if (!projectId) return;

    setError(null);
    try {
      await projectService.addTeamMember(projectId, member);
      await loadProject(); // Refresh project data
    } catch (err: any) {
      setError(err.message || 'Failed to add team member');
      throw err;
    }
  }, [projectId, loadProject]);

  const removeTeamMember = useCallback(async (userId: string) => {
    if (!projectId) return;

    setError(null);
    try {
      await projectService.removeTeamMember(projectId, userId);
      await loadProject(); // Refresh project data
    } catch (err: any) {
      setError(err.message || 'Failed to remove team member');
      throw err;
    }
  }, [projectId, loadProject]);

  const updateTeamMember = useCallback(async (userId: string, updates: Partial<TeamMember>) => {
    if (!projectId) return;

    setError(null);
    try {
      await projectService.updateTeamMember(projectId, userId, updates);
      await loadProject(); // Refresh project data
    } catch (err: any) {
      setError(err.message || 'Failed to update team member');
      throw err;
    }
  }, [projectId, loadProject]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  return {
    project,
    etag,
    loading,
    error,
    loadProject,
    updateProject,
    addTeamMember,
    removeTeamMember,
    updateTeamMember,
  };
}
